import axios from 'axios';
import merge from 'lodash/merge';

import { globalConstants } from '@/constants/global.constants';
import { TranslationsService } from '@/services/translations.service';
import { useUserStore } from '@/stores/user';
import { LocalizedString } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { defaultMessages } from '../lang/default';

export function useLocale() {
  const { setLocaleMessage, locale } = useI18n();
  const { userLocale } = storeToRefs(useUserStore());
  const userLanguage = computed<keyof LocalizedString>(() =>
    getSupportedLocaleLanguage(userLocale?.value ?? globalConstants.fallBackLocale),
  );

  const loadedLanguages: any[] = [];
  const translationsService = new TranslationsService();

  function getSupportedLocaleLanguage(locale: string): keyof LocalizedString {
    const getValidLocale = (language: string) => {
      // at the time all available locales on the shared LocalizedString class are the only valid locale languages,
      // if you want to add a new locale language please modify the class on cil libs and update the lib here as well as on backstage-api
      for (const validLanguage in new LocalizedString()) {
        if (validLanguage.startsWith(language)) {
          return validLanguage as keyof LocalizedString;
        }
      }
    };

    let validLanguage: keyof LocalizedString | undefined = getValidLocale(locale);

    if (!validLanguage) {
      const langCountry = locale.split('-');

      validLanguage = getValidLocale(langCountry[0]);
    }

    // ToDo: review if fallBackLocale is fine, or if it should be reverted back to default
    return validLanguage ?? globalConstants.fallBackLocale;
  }

  function setI18nLocale(localeToLoad: string) {
    if (loadedLanguages.includes(localeToLoad)) {
      if (locale.value !== localeToLoad) {
        setLocale(localeToLoad);
      }

      return Promise.resolve();
    }

    return setI18nLocaleFromServer(localeToLoad);
  }

  function setLocale(selectedLocale: string) {
    locale.value = selectedLocale;
    axios.defaults.headers.common['Accept-Language'] = selectedLocale;
    (document.querySelector('html') as HTMLHtmlElement).setAttribute('lang', selectedLocale);

    if (window.productsUIEvents) {
      window.productsUIEvents.setLanguageAsync(selectedLocale);
    }

    return selectedLocale;
  }

  const setI18nLocaleFromServer = async (selectedLocale: string) => {
    const defaultLanguageMessagesCopy = JSON.parse(JSON.stringify(defaultMessages));

    setLocaleMessage(selectedLocale, defaultLanguageMessagesCopy);
    setLocale(selectedLocale);

    const selectedLanguageMessages = await translationsService.getByLanguage(selectedLocale);

    if (selectedLanguageMessages !== null) {
      loadedLanguages.push(selectedLocale);
      // merge into a new object: vue-i18n only reacts to a changed message reference,
      // and lodash's merge() mutates+returns defaultLanguageMessagesCopy, which was already
      // registered above, so re-passing that same reference would silently no-op.
      setLocaleMessage(selectedLocale, merge({}, defaultLanguageMessagesCopy, selectedLanguageMessages));
    }
  };

  return {
    setI18nLocale,

    /**
     * This is a valid corresponding language to a selected Locale ie. en-US, de, fr
     * Value can be a full locale ie. en-US or just a language ie. de, this depends on how where they
     * defined in contentful
     * If no matching language is found, then user language defaults to en-US
     * Please only use this when you have to manually translate a response ie. `cards.title['userLocale']`
     * For regular translations just use i18n library ie. `$t('myModule.title')`
     */
    userLanguage,

    /**
     * User Locale ie. en-US, en-GB or de-de
     * this value comes originaly from Salesforce, so initial value can be not supported as a language
     * as soon as the user picks a new value on the footer this value will be updated with a supported locale.
     * Use locale for external libraries translations and formating
     */
    userLocale,
  };
}
